import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Spinner from "../../../Components/common/Spinner/Spinner";
import Warning from "../../../Components/common/Warning/Warning";
import ToggleButton from "../../../Components/common/ToggleButton/ToggleButton";
import TableSpinnerIcon from "../../../Components/Icon-SVG/TableSpinnerIcon";

import {
  RequestContainerService,
  RequestInforService,
  RequestVirshService,
  RequestGetAutoUpdateState,
  RequestPodRestart,
  RequestPodUpdate,
  RequestSetAutoUpdateState,
} from "../../../store/actions/containerActions";
import { virshService } from "../../../store/sagas/containerSagas";

const Header = ({ selectedProduct, selectedResponsible, onRefresh, spin }) => {
  const dispatch = useDispatch();

  const isAutoUpdateActive = useSelector((state) => state.container.isAutoUpdateActive);

  useEffect(() => {
    dispatch(RequestGetAutoUpdateState(selectedResponsible));
  }, [dispatch, selectedResponsible]);

  const setAutoUpdateActive = (booleanState) => {
    const newState = booleanState === true ? "on" : "off";
    dispatch(RequestSetAutoUpdateState({ responsible: selectedResponsible, state: newState }));
  };

  return (
    <div className="services-body-header">
      <div className="services-body-header__title">
        <h3>
          {selectedProduct} / <span>{selectedResponsible}</span>
        </h3>
        <div onClick={onRefresh}>
          <TableSpinnerIcon
            className="services-body-header__spinner"
            customStyle={{ margin: "0", height: "20px" }}
            spin={spin}
          />
        </div>
      </div>
      <div className="services-body-header__auto-update">
        <span>Auto Update</span>
        <ToggleButton active={isAutoUpdateActive} onClick={setAutoUpdateActive} />
      </div>
      <p className="services-body-header__description">
        <span> NOTE:</span> You can update and restart pods using buttons
      </p>
    </div>
  );
};

const HeaderInfo = ({ selectedProduct, selectedResponsible, onRefresh, spin }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(RequestGetAutoUpdateState(selectedResponsible));
  }, [dispatch, selectedResponsible]);

  return (
    <div className="services-body-header">
      <div className="services-body-header__title">
          <h3>Resources Info</h3>
        <div onClick={onRefresh}>
          <TableSpinnerIcon
            className="services-body-header__spinner"
            customStyle={{ margin: "0", height: "20px" }}
            spin={spin}
          />
        </div>
      </div>
      <p className="services-body-header__description">
        <span> NOTE:</span> You can find system resources information of server which above k8admin
        container running.
      </p>
    </div>
  );
};

const HeaderVirsh = ({ selectedProduct, selectedResponsible, onRefresh, spin }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(RequestGetAutoUpdateState(selectedResponsible));
  }, [dispatch, selectedResponsible]);

  return (
    <div className="services-body-header">
      <div className="services-body-header__title">
          <h3>Virtual Machines Info</h3>
        <div onClick={onRefresh}>
          <TableSpinnerIcon
            className="services-body-header__spinner"
            customStyle={{ margin: "0", height: "20px" }}
            spin={spin}
          />
        </div>
      </div>
      <p className="services-body-header__description">
        <span> NOTE:</span> You can find status of virtual machines which is used in the core system which k8admin running.
      </p>
    </div>
  );
};

const TITLES = ["NAME", "READY", "STATUS", "RESTARTS", "AGE", "UPDATE", "RESTART"];

const formatTime = (ageString) => {
  if (ageString.includes(",")) {
    return ageString.split(",")[0].trim();
  }

  const timeParts = ageString.split(":");

  if (timeParts.length === 3) {
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const seconds = parseInt(timeParts[2].split(".")[0], 10);

    if (hours > 0) {
      return `${hours} ${hours === 1 ? "hour" : "hours"}`;
    } else if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    } else {
      return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
    }
  }

  return ageString;
};

const List = ({ containerService }) => {
  const dispatch = useDispatch();

  const [showWarning, setShowWarning] = useState(false);
  const [clickedPod, setClickedPod] = useState(null);
  const [clickedAction, setClickedAction] = useState(""); // "update" or "restart"

  const updatePodName = useSelector((state) => state.container.updatePodName);
  const updatePodLoading = useSelector((state) => state.container.updatePodLoading);
  const restartPodName = useSelector((state) => state.container.restartPodName);
  const restartPodLoading = useSelector((state) => state.container.restartPodLoading);

  const updateHandler = (pod) => {
    if (!pod?.image_name) return;
    if (updatePodLoading) return;
    if (restartPodLoading) return;

    const responsible = pod.responsible;
    const imageName = pod.image_name.split("/")[1];
    const podName = pod.name;
    dispatch(RequestPodUpdate({ responsible, imageName, podName }));
  };

  const restartHandler = (pod) => {
    if (restartPodLoading) return;
    if (updatePodLoading) return;

    const responsible = pod.responsible;
    const podName = pod.name;
    dispatch(RequestPodRestart({ responsible, podName }));
  };

  const warningClickOKHandler = () => {
    if (clickedAction === "update") updateHandler(clickedPod);
    else if (clickedAction === "restart") restartHandler(clickedPod);
    setShowWarning(false);
  };

  const updateRestartClickHandler = (pod, action) => {
    setClickedPod(pod);
    setShowWarning(true);
    setClickedAction(action);
  };

  return (
    <>
      <table>
        <thead>
          <tr>
            {TITLES.map((header, i) => (
              <th key={i}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {containerService.map((pod, i) => (
            <tr key={i}>
              <td>{pod.name}</td>
              <td>{pod.ready}</td>
              <td>{pod.status}</td>
              <td>{pod.restarts ?? 0}</td>
              <td>{formatTime(pod.age)}</td>
              <td>
                <button
                  onClick={() => updateRestartClickHandler(pod, "update")}
                  disabled={!pod.update || updatePodLoading || restartPodLoading}>
                  {updatePodLoading && updatePodName === pod.name ? (
                    <Spinner width={12} height={12} />
                  ) : (
                    "Update"
                  )}
                </button>
              </td>
              <td>
                <button
                  onClick={() => updateRestartClickHandler(pod, "restart")}
                  disabled={updatePodLoading || restartPodLoading}>
                  {restartPodLoading && restartPodName === pod.name ? (
                    <Spinner width={12} height={12} />
                  ) : (
                    "Restart"
                  )}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Warning
        onClickOk={warningClickOKHandler}
        show={showWarning}
        setShow={setShowWarning}
        text={`Are you sure you want to ${clickedAction} this pod ?`}
        article={`Confirm ${clickedAction.charAt(0).toUpperCase() + clickedAction.slice(1)}`}
        cancelDisable={false}
      />
    </>
  );
};

const TITLES_INFO = ["NAME", "INFORMATION", "STATUS"];

const ListInfo = ({ inforService }) => {
  const hasUnhealthy = inforService.some((vms) => (vms.status.toLowerCase() !== "normal" && vms.status.toLowerCase() !== "connected"));
  return (
    <>
    {hasUnhealthy && (
        <div
          style={{
            background: "#fff3cd",
            color: "#856404",
            padding: "10px",
            margin: "16px",
            borderRadius: "4px",
            marginBottom: "-10px"
          }}
        >
          ⚠️ Please check this server's status via related console CLI commands
        </div>
      )}
      <table>
        <thead>
          <tr>
            {TITLES_INFO.map((header, i) => (
              <th key={i}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {inforService.map((pod, i) => (
            <tr key={i}>
              <td>{pod.name}</td>
              <td>{pod.ready}</td>
              <td
                style={{
                  color: pod.status.toLowerCase() === "normal" ? "var(--attack-map-popup-button)" : "",
                  fontWeight: "bold",
                }}
              
              >{pod.status.charAt(0).toUpperCase() + pod.status.slice(1)} </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const TITLES_VIRSH = ["NAME", "PATH", "STATUS", "REASON", "AVAILABILITY"];

const ListVirsh = ({ virshService }) => {
  // Check if any VM is not healthy
  const hasUnhealthy = virshService.some((vms) => vms.status.toLowerCase() !== "healthy");

  // Build summary counts based on VM name prefixes
  const summary = virshService.reduce((acc, vms) => {
    const osType = vms.name.split("_")[0]; // take prefix before first "_"
    acc[osType] = (acc[osType] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      {hasUnhealthy && (
        <div
          style={{
            background: "#fff3cd",
            color: "#856404",
            padding: "10px",
            margin: "16px",
            borderRadius: "4px",
            marginBottom: "-10px"
          }}
        >
          ⚠️ Please fix image overlays via console CLI command <code>fixoverlayimages</code>
        </div>
      )}
      <table>
        <thead>
          <tr>
            {TITLES_VIRSH.map((header, i) => (
              <th
                key={i}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {virshService.map((vms, i) => (
            <tr key={i}>
              <td >{vms.name}</td>
              <td >{vms.path}</td>
              <td
                style={{
                  color: vms.status.toLowerCase() === "healthy" ? "var(--attack-map-popup-button)" : "",
                  fontWeight: "bold",
                }}
              >
                {vms.status.charAt(0).toUpperCase() + vms.status.slice(1)}
              </td>
              <td >{vms.reason.charAt(0).toUpperCase() + vms.reason.slice(1)}</td>
              <td
                style={{
                  color: vms.avail ? "var(--attack-map-popup-button)" : "",
                  fontWeight: "bold",
                }}
              >
                {vms.avail ? "Available" : "Unavailable"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        style={{
          marginBottom: "12px",
          marginLeft: "28px",
          color:"white",
        }}
      >
        <strong>Summary of Virtual Machines:</strong>
        <ul style={{marginTop:"1px"}}>
          {Object.entries(summary).map(([os, count]) => (
            <li key={os}>
              {os} has {count} duplicates.
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const VirshWrapper = ({
  selectedProduct,
  selectedResponsible,
  refreshHandler3,
  virshServiceLoading,
  virshService,
}) => {
  if (selectedProduct !== "Core Analyser") {
    return null; // don’t render anything if product doesn’t match
  }

  return (
    <>
      <HeaderVirsh
        selectedProduct={selectedProduct}
        selectedResponsible={selectedResponsible}
        onRefresh={refreshHandler3}
        spin={virshServiceLoading}
      />
      <div className="services-body-content">
        {virshServiceLoading ? (
          <div className="services-body-content__center">
            <Spinner width={24} height={24} />
          </div>
        ) : virshService.length ? (
          <ListVirsh virshService={virshService} />
        ) : (
          <div className="services-body-content__center">
            <p className="services-body-content__no-data">No data - Please click Refresh button to get !</p>
          </div>
        )}
      </div>
    </>
  );
};


const ContainerServicesBody = ({ selectedProduct, selectedResponsible }) => {
  const dispatch = useDispatch();

  const containerService = useSelector((state) => state.container.containerService);
  const inforService = useSelector((state) => state.container.inforService);
  const virshService = useSelector((state) => state.container.virshService);
  const containerServiceLoading = useSelector((state) => state.container.containerServiceLoading);
  const inforServiceLoading = useSelector((state) => state.container.inforServiceLoading);
  const virshServiceLoading = useSelector((state) => state.container.virshServiceLoading);
  const showUpdateMessage = useSelector((state) => state.container.showUpdateMessage);

  useEffect(() => {
    if (!selectedResponsible) return;
    dispatch(RequestContainerService(selectedResponsible));
  }, [dispatch, selectedResponsible]);

  const refreshHandler = () => {
    if (containerServiceLoading) return;
    dispatch(RequestContainerService(selectedResponsible));
  };

  useEffect(() => {
    if (!selectedResponsible) return;
    dispatch(RequestInforService(selectedResponsible));
  }, [dispatch, selectedResponsible]);

  const refreshHandler2 = () => {
    if (inforServiceLoading) return;
    dispatch(RequestInforService(selectedResponsible));
  };

  useEffect(() => {
    if (!selectedResponsible) return;
    if (selectedProduct !== "Core Analyser") return;
    //dispatch(RequestVirshService(selectedResponsible));
  }, [dispatch, selectedResponsible]);

  const refreshHandler3 = () => {
    if (selectedProduct !== "Core Analyser") return;
    if (virshServiceLoading) return;
    dispatch(RequestVirshService(selectedResponsible));
  };

  if (!selectedResponsible)
    return <p className="services-no-select">Please select a product and a host.</p>;

  return (
    <div className="services-body">
      <Header
        selectedProduct={selectedProduct}
        selectedResponsible={selectedResponsible}
        onRefresh={refreshHandler}
        spin={containerServiceLoading}
      />
      <div className="services-body-content">
        {showUpdateMessage ? (
          <div className="services-body-content__center">
            <p className="services-body-content__no-data">
              Update in progress, please refresh the list using reload button above.
            </p>
          </div>
        ) : containerServiceLoading ? (
          <div className="services-body-content__center">
            <Spinner width={24} height={24} />
          </div>
        ) : containerService.length ? (
          <List containerService={containerService} />
        ) : (
          <div className="services-body-content__center">
            <p className="services-body-content__no-data">No data</p>
          </div>
        )}
      </div>
      <HeaderInfo 
        selectedProduct={selectedProduct}
        selectedResponsible={selectedResponsible}
        onRefresh={refreshHandler2}
        spin={inforServiceLoading}
      />
      <div className="services-body-content">
        {inforServiceLoading ? (
          <div className="services-body-content__center">
            <Spinner width={24} height={24} />
          </div>
        ) : inforService.length ? (
          <ListInfo inforService={inforService} />
        ) : (
          <div className="services-body-content__center">
            <p className="services-body-content__no-data">No data</p>
          </div>
        )}
      </div>
      <VirshWrapper
        selectedProduct={selectedProduct}
        selectedResponsible={selectedResponsible}
        refreshHandler3={refreshHandler3}
        virshServiceLoading={virshServiceLoading}
        virshService={virshService}
      />
    </div>
  );
};

export default ContainerServicesBody;
